/**
 * Shared controller for both global-search surfaces (sidebar input +
 * Cmd+K palette). Owns input state, keyboard navigation, and the
 * select-and-navigate flow so the two surfaces stay behaviourally
 * identical.
 *
 * Items are flattened into a single ordered list for keyboard
 * navigation; the renderer regroups them visually but uses each item's
 * `flatIndex` to drive the active highlight.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import {
  useGlobalSearch,
  type UseGlobalSearchResult,
} from './useGlobalSearch';
import {
  useRecentSearchEntities,
  type RecentSearchEntity,
} from './useRecentSearchEntities';
import type {
  CompanyRow,
  PersonRow,
} from '@/features/database/api/databaseApi';
import { normalizePersonName } from '@/utils/displayName';

export type CompanySearchItem = {
  kind: 'company';
  flatIndex: number;
  data: CompanyRow;
};
export type PersonSearchItem = {
  kind: 'person';
  flatIndex: number;
  data: PersonRow;
};
export type RecentSearchItem = {
  kind: 'recent';
  flatIndex: number;
  data: RecentSearchEntity;
};
export type SearchItem = CompanySearchItem | PersonSearchItem | RecentSearchItem;

export interface GlobalSearchSections {
  /** Visible when `isEnabled` is true. */
  companies: CompanySearchItem[];
  people: PersonSearchItem[];
  /** Visible only when query is empty (the "recents" empty state). */
  recents: RecentSearchItem[];
}

export interface UseGlobalSearchControllerOptions {
  /** Called after a successful select, so the host surface can close. */
  onClose?: () => void;
}

export interface UseGlobalSearchControllerResult {
  query: string;
  setQuery: (q: string) => void;
  search: UseGlobalSearchResult;
  sections: GlobalSearchSections;
  /** Flat ordered list of selectable items; activeIndex points into this. */
  flatItems: SearchItem[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  selectAt: (index: number) => void;
  selectItem: (item: SearchItem) => void;
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  reset: () => void;
}

export function useGlobalSearchController(
  opts: UseGlobalSearchControllerOptions = {},
): UseGlobalSearchControllerResult {
  const navigate = useNavigate();
  const [query, setQueryRaw] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const search = useGlobalSearch(query);
  const { recents, addRecent } = useRecentSearchEntities();

  const sections = useMemo<GlobalSearchSections>(() => {
    if (search.isEnabled) {
      const companies: CompanySearchItem[] = search.companies.map((data, i) => ({
        kind: 'company',
        flatIndex: i,
        data,
      }));
      const people: PersonSearchItem[] = search.people.map((data, i) => ({
        kind: 'person',
        flatIndex: companies.length + i,
        data,
      }));
      return { companies, people, recents: [] };
    }
    const recentItems: RecentSearchItem[] = recents.map((data, i) => ({
      kind: 'recent',
      flatIndex: i,
      data,
    }));
    return { companies: [], people: [], recents: recentItems };
  }, [search.isEnabled, search.companies, search.people, recents]);

  const flatItems = useMemo<SearchItem[]>(
    () => [...sections.companies, ...sections.people, ...sections.recents],
    [sections],
  );

  // Reset the highlighted row when the underlying list changes shape.
  // Without this, the highlight can land on an out-of-range index after
  // typing, or persist on a stale row across queries.
  useEffect(() => {
    setActiveIndex(0);
  }, [search.debouncedQuery, search.isEnabled, recents]);

  const setQuery = useCallback((next: string) => {
    setQueryRaw(next);
  }, []);

  const reset = useCallback(() => {
    setQueryRaw('');
    setActiveIndex(0);
  }, []);

  const selectItem = useCallback(
    (item: SearchItem) => {
      if (item.kind === 'company') {
        const c = item.data;
        addRecent({
          kind: 'company',
          id: c.id,
          name: c.display_name?.trim() || c.name,
          subtitle: [c.jurisdiction_code, c.registration_number]
            .filter(Boolean)
            .join(' · '),
          logoUrl: c.logo_url ?? undefined,
          domain: c.domain ?? undefined,
        });
        // TanStack Router's typed-route signature is strict — cast to
        // unblock cross-route navigation from a generic component.
        navigate({
          to: '/database/companies/$companyId',
          params: { companyId: c.id },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      } else if (item.kind === 'person') {
        const p = item.data;
        addRecent({
          kind: 'person',
          id: p.persona_id,
          name: normalizePersonName(p.canonical_name),
          subtitle:
            [p.current_title, p.current_org_name].filter(Boolean).join(' · ') ||
            undefined,
          photoUrl: p.photo_url ?? undefined,
        });
        navigate({
          to: '/database/people/$personaId',
          params: { personaId: p.persona_id },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      } else {
        const r = item.data;
        // Bump visitedAt by re-adding.
        addRecent({
          kind: r.kind,
          id: r.id,
          name: r.name,
          subtitle: r.subtitle,
          logoUrl: r.logoUrl,
          domain: r.domain,
          photoUrl: r.photoUrl,
        });
        if (r.kind === 'company') {
          navigate({
            to: '/database/companies/$companyId',
            params: { companyId: r.id },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
        } else {
          navigate({
            to: '/database/people/$personaId',
            params: { personaId: r.id },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
        }
      }
      reset();
      opts.onClose?.();
    },
    [addRecent, navigate, opts, reset],
  );

  const selectAt = useCallback(
    (index: number) => {
      const item = flatItems[index];
      if (item) selectItem(item);
    },
    [flatItems, selectItem],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (flatItems.length === 0 && e.key !== 'Escape') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(Math.min(activeIndex + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(Math.max(activeIndex - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        selectAt(activeIndex);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (query) {
          reset();
        } else {
          opts.onClose?.();
        }
      }
    },
    [flatItems.length, activeIndex, selectAt, query, reset, opts],
  );

  return {
    query,
    setQuery,
    search,
    sections,
    flatItems,
    activeIndex,
    setActiveIndex,
    selectAt,
    selectItem,
    handleInputKeyDown,
    reset,
  };
}
